import type { AdPlatform } from './types';

// Per-platform facts from the PRD ("Anatomy of ad platforms" and the historical CPL tables).
// Agents and checks read these instead of hard-coding one platform, so LinkedIn and Google
// only need their entries filled in here.

export interface AspectRatio {
  label: string;
  width: number;
  height: number;
}

export interface PlatformSpec {
  label: string;
  /** May–Jul matured CPL for this platform's main campaign type; null when we have none. */
  benchmarkCplUsd: number | null;
  benchmarkNote: string;
  creative: {
    /** Static image ratios the platform accepts; empty when the format has no image. */
    aspectRatios: AspectRatio[];
    /** Video ratios the platform accepts; empty when it takes no video. */
    videoAspectRatios: AspectRatio[];
    /** False when the ad reuses someone else's post, so its copy can't be changed. */
    copyEditable: boolean;
    primaryTextVisibleLimit: number | null;
    headlineLimit: number | null;
    descriptionLimit: number | null;
    notes: string;
  };
}

const SQUARE: AspectRatio = { label: '1:1', width: 1, height: 1 };
const PORTRAIT: AspectRatio = { label: '4:5', width: 4, height: 5 };
const VERTICAL: AspectRatio = { label: '9:16', width: 9, height: 16 };
const LANDSCAPE: AspectRatio = { label: '16:9', width: 16, height: 9 };

export const PLATFORM_SPECS: Record<AdPlatform, PlatformSpec> = {
  meta: {
    label: 'Meta',
    benchmarkCplUsd: 30.34,
    benchmarkNote: 'Meta MOFU content & webinar, 19 campaigns, May–Jul',
    creative: {
      aspectRatios: [SQUARE, PORTRAIT],
      videoAspectRatios: [PORTRAIT, VERTICAL],
      copyEditable: true,
      primaryTextVisibleLimit: 125,
      headlineLimit: 40,
      descriptionLimit: null,
      notes:
        'Feed ads: images 1:1 or 4:5 (mobile); videos 4:5 and 9:16. Keep text on the creative minimal; it should read at thumbnail size, and videos should make sense with the sound off.',
    },
  },
  linkedin: {
    label: 'LinkedIn',
    benchmarkCplUsd: 205.54,
    benchmarkNote: 'LinkedIn MOFU webinar promotion, 25 campaigns, May–Jul',
    creative: {
      aspectRatios: [SQUARE, PORTRAIT],
      videoAspectRatios: [LANDSCAPE, SQUARE, PORTRAIT],
      copyEditable: false,
      primaryTextVisibleLimit: null,
      headlineLimit: null,
      descriptionLimit: null,
      notes: "Boosted posts use the author's copy, which can't be edited. One format per ad set.",
    },
  },
  google: {
    label: 'Google',
    benchmarkCplUsd: null,
    benchmarkNote: 'No Google CPL benchmark recorded yet',
    creative: {
      aspectRatios: [],
      videoAspectRatios: [],
      copyEditable: true,
      primaryTextVisibleLimit: null,
      headlineLimit: 30,
      descriptionLimit: 90,
      notes: 'Search ads are text only: 3–15 headlines of 30 characters and 2–4 descriptions of 90.',
    },
  },
};

/** The decision rules from the PRD's "How we will make dial up / down decisions", same for every platform. */
export const DECISION_RULES =
  'The decision metric is CPL (spend ÷ leads). Qualified-lead rate is the guardrail that can veto a dial-up. Hold fire until the campaign clears a minimum data threshold (a few days of delivery and enough results to be meaningful); before that, say there is not enough data yet.';

const RATIO_TOLERANCE = 0.02;

export type CreativeKind = 'image' | 'video';

export function acceptedAspectRatios(platform: AdPlatform, kind: CreativeKind): AspectRatio[] {
  const { creative } = PLATFORM_SPECS[platform];
  return kind === 'video' ? creative.videoAspectRatios : creative.aspectRatios;
}

export function matchAspectRatio(platform: AdPlatform, width: number, height: number, kind: CreativeKind = 'image'): AspectRatio | null {
  if (width <= 0 || height <= 0) return null;
  const ratio = width / height;
  return acceptedAspectRatios(platform, kind).find(r => Math.abs(ratio - r.width / r.height) <= RATIO_TOLERANCE * (r.width / r.height)) ?? null;
}

/** Meta's Stories and Reels format, for the second version of a creative. */
export const META_VERTICAL_RATIO = VERTICAL;

export function matchesRatio(width: number, height: number, r: AspectRatio): boolean {
  return width > 0 && height > 0 && Math.abs(width / height - r.width / r.height) <= RATIO_TOLERANCE * (r.width / r.height);
}

/** "16:9", "1.43:1" … for telling the requester what they uploaded. */
export function describeAspectRatio(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const d = gcd(width, height);
  const w = width / d;
  const h = height / d;
  return w <= 32 && h <= 32 ? `${w}:${h}` : `${(width / height).toFixed(2)}:1`;
}
